/**
 * VendorQueueView — Conductor → Vendors sub-tab (iter86.52).
 *
 * The vendor onboarding to-do queue. Items are filed by the Vendor-Add
 * Workflow (memory/workflows/vendor-onboarding.md) whenever a vendor/API is
 * added or materially changed and a downstream step can't be completed
 * immediately (missing renewal date, prod verification pending, etc.).
 */
import { useEffect, useState } from "react";
import { Check, Handshake, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STEP_LABELS = {
    a: "a · Vendor Registry row", b: "b · Feature Registry join", c: "c · Env keys / secrets",
    d: "d · Live status", e: "e · Health rollup", f: "f · Compliance & retention",
    g: "g · Env key posture", h: "h · Operator checkpoints", i: "i · Queue hygiene",
};

export function VendorQueueView() {
    const [rows, setRows] = useState(null);
    const [showResolved, setShowResolved] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ vendor_name: "", title: "", detail: "", workflow_step: "" });

    const load = () => api.vendorTodos(showResolved ? "all" : "open")
        .then((r) => setRows(r.todos)).catch(() => setRows([]));
    useEffect(() => { load(); }, [showResolved]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        api.vendorsList().then((r) => setVendors((r.rows || []).map((v) => v.name))).catch(() => {});
    }, []);

    const create = async () => {
        if (!form.vendor_name || !form.title.trim()) { toast.error("Vendor and title are required"); return; }
        try {
            await api.vendorTodoCreate(form);
            toast.success("To-do filed");
            setForm({ vendor_name: "", title: "", detail: "", workflow_step: "" });
            setAdding(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to file to-do");
        }
    };

    const resolve = async (t) => {
        const note = window.prompt(`Resolution note for "${t.title}"?`);
        if (!note) return;
        try {
            await api.vendorTodoResolve(t.id, note);
            toast.success("Resolved");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to resolve");
        }
    };

    return (
        <div className="space-y-3" data-testid="vendor-queue-view">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-xs text-zinc-400 max-w-xl">
                    <span className="text-white font-bold uppercase text-[11px] tracking-wide flex items-center gap-1.5 mb-1">
                        <Handshake className="w-3.5 h-3.5 text-cyber" /> Vendor onboarding queue
                    </span>
                    Downstream to-dos filed by the Vendor-Add Workflow
                    (<span className="font-mono text-[10px] text-zinc-500">memory/workflows/vendor-onboarding.md</span>)
                    when a vendor or API is added or materially changed.
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setShowResolved((v) => !v)}
                        className="px-2.5 py-1 text-[10px] font-bold uppercase border border-zinc-700 rounded text-zinc-400 hover:text-white"
                        data-testid="vendor-queue-toggle-resolved">
                        {showResolved ? "Open only" : "Show resolved"}
                    </button>
                    <button type="button" onClick={() => setAdding((v) => !v)}
                        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase border border-cyber/50 text-cyber rounded hover:bg-cyber/10"
                        data-testid="vendor-todo-add-btn">
                        <Plus className="w-3 h-3" /> File to-do
                    </button>
                </div>
            </div>

            {adding && (
                <div className="border border-zinc-800 rounded p-3 space-y-2 bg-zinc-950/60" data-testid="vendor-todo-form">
                    <div className="grid sm:grid-cols-2 gap-2">
                        <select value={form.vendor_name}
                            onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                            className="bg-black border border-zinc-700 rounded px-2 py-1.5 text-xs"
                            data-testid="vendor-todo-vendor-select">
                            <option value="">— Vendor —</option>
                            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                        <select value={form.workflow_step}
                            onChange={(e) => setForm({ ...form, workflow_step: e.target.value })}
                            className="bg-black border border-zinc-700 rounded px-2 py-1.5 text-xs"
                            data-testid="vendor-todo-step-select">
                            <option value="">— Workflow step (optional) —</option>
                            {Object.entries(STEP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </div>
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Title — what needs doing"
                        className="w-full bg-black border border-zinc-700 rounded px-2 py-1.5 text-xs"
                        data-testid="vendor-todo-title-input" />
                    <input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })}
                        placeholder="Detail (optional)"
                        className="w-full bg-black border border-zinc-700 rounded px-2 py-1.5 text-xs"
                        data-testid="vendor-todo-detail-input" />
                    <button type="button" onClick={create}
                        className="px-3 py-1.5 text-[10px] font-bold uppercase bg-cyber text-black rounded"
                        data-testid="vendor-todo-submit-btn">
                        File
                    </button>
                </div>
            )}

            {rows === null && <div className="text-xs text-zinc-500">Loading…</div>}
            {rows !== null && rows.length === 0 && (
                <div className="text-xs text-zinc-500 border border-dashed border-zinc-800 rounded p-4" data-testid="vendor-queue-empty">
                    Queue clear — no open vendor onboarding items.
                </div>
            )}
            {(rows || []).map((t) => (
                <div key={t.id}
                    className={`border rounded p-3 flex items-start justify-between gap-3 ${t.status === "resolved" ? "border-zinc-800 opacity-60" : "border-zinc-700 bg-zinc-950/60"}`}
                    data-testid="vendor-todo-row">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="pill text-[9px] font-bold uppercase border border-cyber/40 text-cyber">{t.vendor_name}</span>
                            {t.workflow_step && (
                                <span className="pill text-[9px] border border-zinc-700 text-zinc-400">{STEP_LABELS[t.workflow_step] || t.workflow_step}</span>
                            )}
                            {t.status === "resolved" && (
                                <span className="pill text-[9px] font-bold uppercase border border-emerald-700 text-emerald-400">Resolved</span>
                            )}
                        </div>
                        <div className="text-xs text-white font-bold mt-1.5">{t.title}</div>
                        {t.detail && <div className="text-[11px] text-zinc-400 mt-0.5">{t.detail}</div>}
                        <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                            {new Date(t.ts).toLocaleString()} · {t.created_by}
                            {t.status === "resolved" && ` → ${t.resolved_by}: ${t.resolution_note}`}
                        </div>
                    </div>
                    {t.status === "open" && (
                        <button type="button" onClick={() => resolve(t)}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase border border-emerald-700/60 text-emerald-400 rounded hover:bg-emerald-900/20 shrink-0"
                            data-testid="vendor-todo-resolve-btn">
                            <Check className="w-3 h-3" /> Resolve
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
